import { ComponentPortal, Portal, TemplatePortal } from '@angular/cdk/portal';
import { ComponentFactoryResolver, ComponentRef, Injectable, TemplateRef, Type, ViewContainerRef } from '@angular/core';
import { ContextMenuElement } from '@m4u-angular/base/classes/BaseClasses';
import { BaseDataSourceAbstract } from '@m4u-angular/base/datalayer/BaseDataSourceAbstract';
import { BottomSheetContextMenuComponent } from '@m4u-angular/ui/components/bottom-sheet-context-menu/bottom-sheet-context-menu.component';
import { BottomSheetListComponent } from '@m4u-angular/ui/components/bottom-sheet-list/bottom-sheet-list.component';
import { BottomSheetDialogComponent } from '@m4u-angular/ui/dialogs/bottom-sheet-dialog/bottom-sheet-dialog.component';
import { AppInjector } from '../injector/injector.module';
import { EnumWindowSizes } from './sysenums.enum';

export class BottomSheetContentParams {
    Name: string = 'Default';
    Content: Type<IBottomSheetComponent> | TemplateRef<any>;
    Title: string;
    Class: string;
    ContentParams?: any;
    Height?: EnumWindowSizes;
    OnCompleted: (e) => void;
    OnCanceled: () => void;
}

export class BottomSheetListParams {
    Title: string;
    DataSource: BaseDataSourceAbstract | any[];
    IsMultiSelection: boolean;
    ValueExpr: string = 'EntryID';
    DisplayExpr: string | ((item) => string) = 'Name';
    ListTemplate: TemplateRef<any>;
    Value: any;
    IsSearchEnabled: boolean;
    SearchExpr: string[];
    OnCompleted: (e) => void;
    OnCanceled: () => void;
}

export class BottomSheetContextMenuParams {
    Title: string;
    Items: ContextMenuElement[];
    OnCompleted: (e) => void;
    OnCanceled: () => void;
}

export interface IBottomSheetComponent {
    Context: BottomSheetDialogComponent;
}

@Injectable({
    providedIn: 'root'
})
export class BottomSheetService {
    public ViewContainerRef: ViewContainerRef;

    constructor(private componentFactoryResolver: ComponentFactoryResolver) { }

    Show(contentParams: BottomSheetContentParams, keepAlive?: boolean) {
        const bottomSheet = this.CreateBottomSheetInstance();
        const bottomSheetDialog = bottomSheet.instance;
        bottomSheetDialog.ContentPortal = this.ResolveContent(contentParams.Content);
        bottomSheetDialog.Title = contentParams.Title;
        bottomSheetDialog.Class = contentParams.Class;
        bottomSheetDialog.ContentParams = {
            ...contentParams.ContentParams,
            Context: bottomSheetDialog
        };
        bottomSheetDialog.Height = contentParams.Height || EnumWindowSizes.WindowAutoSize;
        bottomSheetDialog.OnClose = () => {
            !keepAlive && bottomSheet.destroy();
            return true;
        };
        
        const randomName = Math.random().toString(36).substring(2, 5);
        bottomSheetDialog.ComponentName = `${bottomSheetDialog.ComponentName}_${contentParams.Name}_${randomName}`;

        bottomSheetDialog.ShowBottomSheet(contentParams.OnCompleted, contentParams.OnCanceled);
    }

    ShowList(listParams: BottomSheetListParams) {
        const contentParams = new BottomSheetContentParams();
        contentParams.Name = 'BottomSheetList';
        contentParams.Content = BottomSheetListComponent;
        contentParams.Title = listParams.Title;
        contentParams.ContentParams = {
            ListParams: listParams
        };
        contentParams.OnCompleted = listParams.OnCompleted;
        contentParams.OnCanceled = listParams.OnCanceled;

        this.Show(contentParams);
    }

    ShowContextMenu(contextMenuParams: BottomSheetContextMenuParams) {
        const contentParams = new BottomSheetContentParams();
        contentParams.Name = 'BottomSheetContextMenu';
        contentParams.Content = BottomSheetContextMenuComponent;
        contentParams.Title = contextMenuParams.Title;
        contentParams.ContentParams = {
            MenuItems: contextMenuParams.Items
        };
        contentParams.OnCompleted = contextMenuParams.OnCompleted;
        contentParams.OnCanceled = contextMenuParams.OnCanceled;

        this.Show(contentParams);
    }

    private CreateBottomSheetInstance(): ComponentRef<BottomSheetDialogComponent> {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(BottomSheetDialogComponent);
        const bottomSheetDialogComponent = this.ViewContainerRef.createComponent(componentFactory);
        return bottomSheetDialogComponent;
    }

    private ResolveContent(content: Type<any> | TemplateRef<any>): Portal<any> {
        return content instanceof TemplateRef
            ? new TemplatePortal(content, this.ViewContainerRef)
            : new ComponentPortal(content, this.ViewContainerRef, AppInjector, this.componentFactoryResolver);
    }

}